import appLogo from '@/assets/icons/app-logo-dark.png';
import { fetchUserMenu } from '@/components/shared/shellRequests';
import { ApiProvider } from '@/provider/ApiProvider';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, Container, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export type FooterProps = {
  brand_id?: string;
  property_id?: string;
  dbsProgress?: boolean;
};

const Footer = ({ dbsProgress = false }: FooterProps) => {
  const userData = ApiProvider.returnUserData();

  const [sessionPopup, setSessionPopup] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState('');
  const [dbsProgressPopup, setDbsProgressPopup] = useState(false);
  const [dbsValidationMsg, setDbsValidationMsg] = useState('');
  const [dbsValidationMsgArray, setDbsValidationMsgArray] = useState('');
  const copyrightYear = new Date().getFullYear();

  function GetUserMenu() {
    // returnUserData() can be null (pre-login / not-yet-hydrated); bail rather
    // than throw "reading 'id' of null" on every render.
    if (!userData?.id) return;
    // the same request on every page: asked once per session (slow pages U3); an
    // 'Unauthorized' answer is not kept, so each page still shows the dialog
    fetchUserMenu().then(
      (response) => {
        if (!response.success) {
          if (response.message == 'Unauthorized') {
            setSessionPopup(true);
          }
        }
      },
      (error) => {
        ApiProvider.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  }

  function logout() {
    ApiProvider.logoutUserLocally(true);
  }

  //---------dbs upload percentage---------------
  function dbsUploadPercentage() {
    const dbsUploadId = localStorage.getItem('dbs_upload_id');

    if (!dbsUploadId) return;

    importProgress(dbsUploadId);
  }

  function importProgress(dbsUploadId: string) {
    ApiProvider.webserviceGet('dbs/getImportProgress/' + dbsUploadId).then(
      (response) => {
        if (response.success == true) {
          const uploadMsg = response.message;
          setUploadPercentage(uploadMsg);
          if (response.data.progress < 100) {
            setTimeout(() => {
              importProgress(dbsUploadId);
            }, 10000);
          } else if (response.data.progress == 100) {
            setDbsValidationMsg(response.data.validationError);
            setDbsValidationMsgArray(response.data.errorMsgs);
            setDbsProgressPopup(true);
            localStorage.removeItem('dbs_datasource_type');
            localStorage.removeItem('dbs_upload_percent');
            localStorage.removeItem('dbs_upload_id');
          } else {
            localStorage.removeItem('dbs_datasource_type');
            localStorage.removeItem('dbs_upload_percent');
            localStorage.removeItem('dbs_upload_id');
          }
        } else {
          setUploadPercentage('');
        }
      },
      (error) => {
        ApiProvider.globalToastMessage(error, 5000, 'error');
        return;
      },
    );
  }

  function dbsClosePopup() {
    setDbsProgressPopup(false);
    setUploadPercentage('');
  }

  useEffect(() => {
    GetUserMenu();

    const dbsUploadIdCheck = localStorage.getItem('dbs_upload_id');
    if (dbsProgress && dbsUploadIdCheck) {
      dbsUploadPercentage();
    }
  }, [dbsProgress]);

  return (
    <>
      <Dialog
        className={'logoutsessionpopup'}
        open={sessionPopup}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>{'Session Expired'}</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            <strong> Your session has been expired!</strong> Please login again to continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={logout} color='primary' autoFocus>
            okay
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dbsProgressPopup}
        onClose={dbsClosePopup}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>{'File import Progress'}</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            File imported Successfully !
            <br />
            {dbsValidationMsg}
            <br />
            {dbsValidationMsgArray}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={dbsClosePopup} color='primary' autoFocus>
            okay
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        className='page_footer'
        sx={{
          marginTop: 'auto',
          backgroundColor: 'tertiary.main',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px 18px',
            flexWrap: 'wrap',
            paddingTop: '12px',
            paddingBottom: '12px',
          }}
        >
          <Box sx={{ position: 'relative', pl: '20px' }}>
            <Typography color='text.secondary' variant='body2'>
              <LanguageIcon
                sx={{
                  fontSize: '1rem',
                  position: 'absolute',
                  left: 0,
                  top: 2,
                  color: 'primary.main',
                }}
              />
              English (US)
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              <strong>{uploadPercentage}</strong>
            </Typography>
          </Box>

          <Box>
            <Typography
              color='text.secondary'
              variant='body2'
              sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
            >
              <img
                alt='Partnership Net logo'
                className='footer_logo'
                src={appLogo}
                style={{ opacity: 0.9 }}
              />
              <Link to={'/Termsofuse/'}>
                <Box component='span' color='secondary.main' fontWeight={600}>
                  Terms of use{' '}
                </Box>
              </Link>{' '}
              | Copyright {copyrightYear} Partnership Net
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Footer;
